package br.com.htms.inspeccaocampo.data.colaboradores

import br.com.htms.inspeccaocampo.auth.AuthUser
import br.com.htms.inspeccaocampo.data.AppConfig

/** Limite de uma TAG dentro de Colaboradores QSMS. Sem registro = módulo inteiro. */
data class ColabEscopoTag(
    val tagId: String,
    val limitado: Boolean = false,
    val abas: List<String> = emptyList(),
    val docs: List<String> = emptyList(),
    /** Não edita cadastro/regras/importação; emissão de documentos segue as abas DOC_*. */
    val somenteVer: Boolean = true,
    /** Lista só o centro de custo do próprio cadastro na matriz de colaboradores. */
    val filtrarCcProprio: Boolean = false
)

/** Teto efetivo de quem está logado. null = sem limite de TAG. */
data class ColabVisao(
    val abas: Set<String>,
    val docs: Set<String>,
    val somenteVer: Boolean,
    val filtrarCcProprio: Boolean = false
) {
    fun mostra(chave: String): Boolean = chave in abas

    fun doc(key: String): Boolean = key in docs
}

object ColabEscopo {
    const val DOC_FUNCIONARIOS = "DOC_FUNCIONARIOS"
    const val DOC_OS = "DOC_OS"
    const val DOC_EPI = "DOC_EPI"
    const val DOC_CERTIFICADO = "DOC_CERTIFICADO"
    const val DOC_APR = "DOC_APR"
    const val DOC_DASHBOARD = "DOC_DASHBOARD"
    const val DOC_IMPORTAR = "DOC_IMPORTAR"

    val abasMatriz: List<Pair<String, String>> = listOf(
        "PAINEL" to "Painel",
        "DASHBOARD" to "Dashboard",
        "COLABORADORES" to "Colaboradores",
        "VENCIMENTOS" to "Vencimentos",
        "CNH" to "CNH",
        "PENDENCIAS" to "Pendências",
        "REGRAS" to "Regras de validade",
        "BACKUP" to "Backup e importação"
    )

    val abasEmissao: List<Pair<String, String>> = listOf(
        DOC_OS to "Ordem de Serviço",
        DOC_EPI to "Ficha de EPI",
        DOC_CERTIFICADO to "Certificados",
        DOC_APR to "APR",
        DOC_DASHBOARD to "Dashboard de documentos",
        DOC_IMPORTAR to "Importar planilha"
    )

    private val abasValidas = (abasMatriz + abasEmissao).map { it.first }.toSet()
    private val docsValidos = DocsMatriz.todos.map { it.key }.toSet()

    fun padraoDp(): ColabEscopoTag = ColabEscopoTag(
        tagId = "DP",
        limitado = true,
        abas = listOf("COLABORADORES"),
        docs = listOf("ASO", "OS"),
        somenteVer = true,
        filtrarCcProprio = false
    )

    fun padraoTst(): ColabEscopoTag = ColabEscopoTag(
        tagId = "TST",
        limitado = true,
        abas = listOf(
            "COLABORADORES",
            "VENCIMENTOS",
            DOC_OS,
            DOC_EPI,
            DOC_CERTIFICADO,
            DOC_APR
        ),
        docs = DocsMatriz.todos.map { it.key },
        somenteVer = true,
        filtrarCcProprio = true
    )

    fun sanitizar(escopo: ColabEscopoTag): ColabEscopoTag {
        val id = escopo.tagId.trim().uppercase()
        return escopo.copy(
            tagId = id,
            abas = escopo.abas.map { it.trim().uppercase() }.filter { it in abasValidas }.distinct(),
            docs = escopo.docs.map { it.trim().uppercase() }.filter { it in docsValidos }.distinct()
        )
    }

    /** Gravado na config, ou o padrão da DP/TST quando a TAG ainda não foi ajustada. */
    fun daTag(config: AppConfig, tagId: String): ColabEscopoTag {
        val id = tagId.trim().uppercase()
        config.colabEscopos.find { it.tagId.equals(id, ignoreCase = true) }?.let { return sanitizar(it) }
        if (id == "DP") return padraoDp()
        if (id == "TST") return padraoTst()
        return ColabEscopoTag(tagId = id, limitado = false)
    }

    /**
     * Quem tem alguma TAG sem limite continua com o módulo inteiro.
     * Quem só tem TAGs limitadas vê a união das abas e dos documentos.
     */
    fun visao(user: AuthUser?, config: AppConfig): ColabVisao? {
        if (user == null || user.isMaster) return null
        val tags = user.effectiveProfileTypes
        if (tags.isEmpty()) return null
        val regras = tags.map { daTag(config, it) }
        if (regras.any { !it.limitado }) return null
        return ColabVisao(
            abas = regras.flatMap { it.abas }.toSet(),
            docs = regras.flatMap { it.docs }.toSet(),
            somenteVer = regras.all { it.somenteVer },
            filtrarCcProprio = regras.any { it.filtrarCcProprio }
        )
    }

    fun mostra(user: AuthUser?, config: AppConfig, chave: String): Boolean {
        val v = visao(user, config) ?: return true
        return v.mostra(chave)
    }
}
